import { EMPTY, Observable } from 'rxjs';
import { expand, last, map } from 'rxjs/operators';
import { TRAIL_MAX_ROWS } from './trail.presenter';

/**
 * REV-22161 (SRR REV-22118 F6) — the page size the pane asks for, sent as `limit` on every trail
 * request. It is the service's own default (`TRAIL_PAGE_DEFAULT_LIMIT`, `abcp-run-query.dto.ts:6`),
 * sent explicitly so that {@link TRAIL_MAX_PAGES} is computed from the size actually requested and not
 * from a default the service may change on its own.
 */
export const TRAIL_PAGE_SIZE = 50;

/**
 * REV-22161 — the hard ceiling on requests per run: enough pages for `TRAIL_MAX_ROWS` plus one.
 *
 * The row cap alone does not end a loop whose pages bring nothing; this one counts requests, so it
 * holds whatever the pages contain.
 */
export const TRAIL_MAX_PAGES = Math.ceil(TRAIL_MAX_ROWS / TRAIL_PAGE_SIZE) + 1;

/** The part of a `RunTrailPage` the paging reads (`AiAbcpTrailPage`, narrowed). */
export interface TrailPageLike<E> {
    entries: E[];
    nextSequence: number | null;
    total: number;
}

/** A run's trail as far as the paging fetched it. */
export interface CollectedTrail<E> {
    /** At most `TRAIL_MAX_ROWS`, in the order the service delivered them. */
    entries: E[];
    /** The run's REAL total from the last page — the cap belongs to the fetch, not to the truth. */
    total: number;
}

/**
 * Whether to fetch another page, and from where: the sequence to send as `sinceSequence`, or `null`
 * to stop.
 *
 * The service sets `nextSequence` on every FULL page — `entries.length === limit` — to the page's
 * last sequence, and `null` on a short one (`abcp-runs.controller.ts:114`, aumico-ms-ai-agents). A
 * trail of exactly n × `limit` rows therefore ends with one EMPTY page carrying `null`. A healthy
 * service never sends an empty page WITH a `nextSequence`, nor a cursor at or behind `sinceSequence`.
 * Any of these ends the paging on its own, whatever `nextSequence` claims (REV-22161):
 *
 *  - `nextSequence` is `null` — the trail is exhausted (the normal end).
 *  - `nextSequence` is not a finite number — missing, `NaN`, a string. The type says `number | null`,
 *    but the page is a network response: a cursor that cannot be compared is not followed, or the
 *    first page would be fetched again and again with its rows doubled (Code-Review REV-22161 R9).
 *  - the page is EMPTY — a page that says "there is more" and brings nothing would otherwise be
 *    asked again and again: the rows never grow, so the row cap never fires. That was the hang of
 *    e2e case 30 on 30.09.2026.
 *  - `nextSequence` does not move past `sinceSequence` — the cursor points back at this page or
 *    before it.
 *  - `TRAIL_MAX_ROWS` rows are in hand, or `TRAIL_MAX_PAGES` requests have gone out.
 */
export function nextTrailSequence(
    page: TrailPageLike<unknown>,
    sinceSequence: number | undefined,
    rowsSoFar: number,
    pagesSoFar: number,
): number | null {
    if (
        page.nextSequence === null ||
        typeof page.nextSequence !== 'number' ||
        !Number.isFinite(page.nextSequence) ||
        page.entries.length === 0
    ) {
        return null;
    }
    if (sinceSequence !== undefined && page.nextSequence <= sinceSequence) {
        return null;
    }
    if (rowsSoFar >= TRAIL_MAX_ROWS || pagesSoFar >= TRAIL_MAX_PAGES) {
        return null;
    }
    return page.nextSequence;
}

/**
 * Follows a run's trail page by page until {@link nextTrailSequence} says stop, then emits ONCE what
 * was collected. `fetchPage(sinceSequence)` is one request — `undefined` for the first page.
 *
 * The pages go out one after the other, never in parallel: each cursor comes from the page before
 * it. An error on any page errors the whole result — a trail with a hole in the middle is not a
 * shorter trail.
 */
export function collectTrailPages$<E>(
    fetchPage: (sinceSequence: number | undefined) => Observable<TrailPageLike<E>>,
): Observable<CollectedTrail<E>> {
    interface Step {
        page: TrailPageLike<E>;
        sinceSequence: number | undefined;
        pages: number;
        entries: E[];
    }
    const fetchStep = (sinceSequence: number | undefined, before: Step | null): Observable<Step> =>
        fetchPage(sinceSequence).pipe(
            map((page) => ({
                page,
                sinceSequence,
                pages: (before?.pages ?? 0) + 1,
                entries: [...(before?.entries ?? []), ...page.entries],
            })),
        );

    return fetchStep(undefined, null).pipe(
        expand((step) => {
            const next = nextTrailSequence(step.page, step.sinceSequence, step.entries.length, step.pages);
            return next === null ? EMPTY : fetchStep(next, step);
        }),
        last(),
        map((step) => ({ entries: step.entries.slice(0, TRAIL_MAX_ROWS), total: step.page.total })),
    );
}
